<template>
  <el-container class="workspace-page employee-list-page">
    <el-main class="workspace-content">
      <div class="employee-filter-bar">
        <el-checkbox v-model="activeOnly" @change="onFilterChange">{{ $t("只看活跃用户") }}</el-checkbox>
        <span class="employee-count">
          <i class="el-icon-user" aria-hidden="true"></i>
          {{ totalCountText }}
        </span>
      </div>
      <div v-if="loading && !employees.length" class="employee-cards employee-cards--loading" aria-hidden="true">
        <div v-for="index in 6" :key="index" class="employee-card-skeleton">
          <div class="employee-card-skeleton__header">
            <span class="employee-card-skeleton__avatar"></span>
            <span class="employee-card-skeleton__name"></span>
          </div>
          <span class="employee-card-skeleton__metric"></span>
          <span class="employee-card-skeleton__meta"></span>
        </div>
      </div>
      <div v-else class="employee-cards">
        <el-card
          v-for="employee in employees"
          :key="employee.userId"
          class="employee-card"
          shadow="never"
          role="article"
          :aria-label="employee.name || '—'">
          <div class="employee-card-header">
            <span class="employee-avatar" aria-hidden="true">{{ employeeInitial(employee) }}</span>
            <div class="employee-identity">
              <div class="employee-card-name">{{ employee.name || '—' }}</div>
              <div class="employee-last-activity" :class="activityStateClass(employee.lastIssueAt)">
                <span class="employee-activity-dot" aria-hidden="true"></span>
                <span>{{ $t("最近记录问题") }}</span>
                <strong>{{ formatReadableDate(employee.lastIssueAt) }}</strong>
              </div>
            </div>
          </div>
          <div class="employee-activity-metric">
            <div class="employee-activity-metric__copy">
              <span>{{ $t("近1个月记录问题数") }}</span>
              <strong>
                {{ employee.recentIssueCount || 0 }}
                <small>{{ $t("条") }}</small>
              </strong>
            </div>
            <div class="employee-activity-bar" aria-hidden="true">
              <span :style="{ width: issueActivityWidth(employee) }"></span>
            </div>
          </div>
          <div class="employee-card-meta">
            <i class="el-icon-time" aria-hidden="true"></i>
            <span class="employee-card-label">{{ $t("注册时间") }}</span>
            <span class="employee-card-value">{{ employee.registerDate || '—' }}</span>
          </div>
        </el-card>
      </div>
      <div v-if="!employees.length && !loading" class="employee-empty">{{ $t("暂无员工数据") }}</div>
      <div class="load-more">
        <i v-if="loading" class="el-icon-loading load-more-spinner"></i>
        <span v-else-if="!hasMore && employees.length" class="load-more-tip">{{ $t("已加载全部") }}</span>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';
import app from '@/App';

export default {
  name: 'EmployeeList',
  data() {
    return {
      user: {},
      activeOnly: true,
      employees: [],
      pageSize: 20,
      nextCursor: null,
      hasMore: true,
      loading: false,
      totalCount: null,
      countLoading: false,
      scrollHandler: null,
    };
  },
  created() {
    app.methods.getUser().then((res) => {
      this.user = res.data;
      this.fetchEmployees(true);
      this.fetchTotalCount();
    });
    this.scrollHandler = () => {
      if (this.loading || !this.hasMore) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const fullHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      if (scrollTop + viewportHeight >= fullHeight - 140) {
        this.fetchEmployees(false);
      }
    };
    window.addEventListener('scroll', this.scrollHandler);
  },
  beforeDestroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  },
  methods: {
    onFilterChange() {
      this.fetchEmployees(true);
      this.fetchTotalCount();
    },
    parseDate(value) {
      if (!value) return null;
      let normalized = value;
      if (typeof normalized === 'string' && normalized.includes(' ') && !normalized.includes('T')) {
        normalized = normalized.replace(' ', 'T');
      }
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return null;
      return date;
    },
    formatReadableDate(value) {
      if (!value) return '—';
      const parsed = this.parseDate(value);
      if (!parsed) return value;
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const diffMs = startOfToday.getTime() - startOfDate.getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days < 0) return value;
      if (days === 0) return t("今天");
      if (days === 1) return t("昨天");
      if (days < 30) return t("{p0}天前", { p0: days });
      if (days < 365) return t("{p0}个月前", { p0: Math.max(1, Math.floor(days / 30)) });
      return t("{p0}年前", { p0: Math.max(1, Math.floor(days / 365)) });
    },
    employeeInitial(employee) {
      const name = String(employee?.name || '').trim();
      return name ? Array.from(name)[0].toUpperCase() : '?';
    },
    activityStateClass(value) {
      const parsed = this.parseDate(value);
      if (!parsed) return 'employee-last-activity--quiet';
      const days = Math.floor((Date.now() - parsed.getTime()) / 86400000);
      if (days <= 7) return 'employee-last-activity--recent';
      if (days <= 30) return 'employee-last-activity--warm';
      return 'employee-last-activity--quiet';
    },
    issueActivityWidth(employee) {
      const count = Math.max(0, Number(employee?.recentIssueCount) || 0);
      if (!count || !this.maxRecentIssueCount) return '0%';
      return `${Math.max(10, Math.round((count / this.maxRecentIssueCount) * 100))}%`;
    },
    fetchEmployees(reset = true) {
      if (this.loading) return;
      if (!reset && !this.hasMore) return;
      this.loading = true;
      if (reset) {
        this.employees = [];
        this.nextCursor = null;
        this.hasMore = true;
      }
      axios
        .get(process.env.VUE_APP_BASE_URL + '/users/employee-list', {
          params: {
            activeOnly: this.activeOnly,
            pageSize: this.pageSize,
            lastIssueAt: this.nextCursor?.lastIssueAt,
            lastUserId: this.nextCursor?.lastUserId,
          },
        })
        .then((res) => {
          const payload = res.data || {};
          const records = Array.isArray(payload.records) ? payload.records : [];
          this.employees = reset ? records : this.employees.concat(records);
          this.nextCursor = payload.nextCursor || null;
          this.hasMore = Boolean(this.nextCursor && records.length > 0);
        })
        .catch((error) => {
          const message = error?.response?.data?.message || t("获取员工列表失败");
          this.$message.error(message);
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            this.$router.replace({ path: '/login' });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchTotalCount() {
      if (this.countLoading) return;
      this.countLoading = true;
      axios
        .get(process.env.VUE_APP_BASE_URL + '/users/employee-list/count', {
          params: {
            activeOnly: this.activeOnly,
          },
        })
        .then((res) => {
          const total = Number(res?.data?.total);
          this.totalCount = Number.isFinite(total) ? total : 0;
        })
        .catch(() => {
          this.totalCount = null;
        })
        .finally(() => {
          this.countLoading = false;
        });
    },
  },
  computed: {
    maxRecentIssueCount() {
      return this.employees.reduce((maximum, employee) => {
        return Math.max(maximum, Number(employee?.recentIssueCount) || 0);
      }, 0);
    },
    totalCountText() {
      if (this.countLoading) return t("统计中...");
      if (this.totalCount === null || this.totalCount === undefined) return t("共 — 人");
      return t("共 {p0} 人", { p0: this.totalCount });
    },
  },
};
</script>

<style scoped>
.employee-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.employee-count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted);
  font-size: 14px;
}

.employee-count i {
  color: var(--primary-color);
  font-size: 15px;
}

.employee-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 18px;
}

.employee-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border-light);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.employee-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(180deg, var(--primary-color), rgba(7, 134, 201, 0.15));
  content: '';
}

.employee-card:hover {
  border-color: rgba(7, 134, 201, 0.3);
  box-shadow: 0 14px 34px rgba(19, 50, 76, 0.12);
  transform: translateY(-2px);
}

.employee-card /deep/ .el-card__body {
  padding: 20px 20px 17px 22px;
}

.employee-card-header {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.employee-avatar {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(7, 134, 201, 0.14);
  border-radius: 14px;
  background: linear-gradient(145deg, var(--primary-soft), var(--surface-hover));
  color: var(--primary-color);
  font-size: 17px;
  font-weight: 700;
}

.employee-identity {
  min-width: 0;
}

.employee-card-name {
  overflow: hidden;
  margin-bottom: 5px;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 650;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-last-activity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.4;
}

.employee-last-activity strong {
  overflow: hidden;
  color: var(--text-regular);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-activity-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--text-muted);
}

.employee-last-activity--recent .employee-activity-dot {
  background: #28a86b;
  box-shadow: 0 0 0 4px rgba(40, 168, 107, 0.1);
}

.employee-last-activity--warm .employee-activity-dot {
  background: #e99a31;
  box-shadow: 0 0 0 4px rgba(233, 154, 49, 0.1);
}

.employee-activity-metric {
  margin: 18px 0 14px;
  padding: 14px;
  border: 1px solid var(--border-light);
  border-radius: 13px;
  background: linear-gradient(135deg, var(--primary-soft), var(--surface-hover) 72%);
}

.employee-activity-metric__copy {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.employee-activity-metric__copy > span {
  color: var(--text-regular);
  font-size: 13px;
  line-height: 1.45;
}

.employee-activity-metric__copy strong {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  color: var(--primary-color);
  font-size: 26px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.employee-activity-metric__copy small {
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 500;
}

.employee-activity-bar {
  height: 4px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--border-light);
}

.employee-activity-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--primary-color), #4eb7da);
  transition: width 0.3s ease;
}

.employee-card-meta {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  font-size: 13px;
}

.employee-card-meta i {
  color: var(--text-muted);
  font-size: 14px;
}

.employee-card-label {
  color: var(--text-muted);
}

.employee-card-value {
  overflow: hidden;
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-card-skeleton {
  padding: 20px;
  border: 1px solid var(--border-light);
  border-radius: 16px;
  background: var(--surface-color);
}

.employee-card-skeleton__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-card-skeleton__avatar,
.employee-card-skeleton__name,
.employee-card-skeleton__metric,
.employee-card-skeleton__meta {
  display: block;
  border-radius: 9px;
  background: linear-gradient(90deg, var(--surface-hover), var(--border-light), var(--surface-hover));
  background-size: 200% 100%;
  animation: employee-card-loading 1.4s ease infinite;
}

.employee-card-skeleton__avatar {
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 14px;
}

.employee-card-skeleton__name {
  width: 46%;
  height: 15px;
}

.employee-card-skeleton__metric {
  height: 72px;
  margin: 18px 0 14px;
  border-radius: 13px;
}

.employee-card-skeleton__meta {
  width: 72%;
  height: 12px;
}

@keyframes employee-card-loading {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.employee-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 16px 0 8px;
}

.load-more {
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
}

.load-more-spinner {
  font-size: 18px;
  color: #909399;
}

.load-more-tip {
  color: #909399;
  font-size: 12px;
}

@media (max-width: 1024px) {
  .employee-cards { gap: 14px; }
}

@media (max-width: 768px) {
  .employee-cards {
    grid-template-columns: 1fr;
  }

  .employee-card:hover {
    transform: none;
  }

  .employee-filter-bar {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: reduce) {
  .employee-card,
  .employee-activity-bar span,
  .employee-card-skeleton__avatar,
  .employee-card-skeleton__name,
  .employee-card-skeleton__metric,
  .employee-card-skeleton__meta {
    animation: none;
    transition: none;
  }
}
</style>
